import React, { useState, useEffect } from "react";
import {
  Card,
  Button,
  Input,
  Spinner,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  useDisclosure,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Tabs,
  Tab,
  Chip,
} from "@heroui/react";
import {
  Plus,
  Search,
  Filter,
  AlertTriangle,
  Download,
  Upload,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

import { inventoryService } from "@/services/inventoryService";
import { formatCurrency } from "@/utils/formatters";
import InventoryTable from "@/components/inventory/InventoryTable";
import StockAdjustmentModal from "@/components/inventory/StockAdjustmentModal";

export default function InventoryPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [ingredients, setIngredients] = useState<any[]>([]);
  const [filteredIngredients, setFilteredIngredients] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [showLowStock, setShowLowStock] = useState(false);
  const [inventorySummary, setInventorySummary] = useState<any>(null);
  const [error, setError] = useState("");

  // Modals
  const {
    isOpen: isStockModalOpen,
    onOpen: onStockModalOpen,
    onClose: onStockModalClose,
  } = useDisclosure();

  const {
    isOpen: isAddIngredientOpen,
    onOpen: onAddIngredientOpen,
    onClose: onAddIngredientClose,
  } = useDisclosure();

  const [newIngredient, setNewIngredient] = useState({
    name: "",
    code: "",
    description: "",
    unit: "unit",
    minimum_stock: "0",
    cost_per_unit: "0",
    supplier: "",
    current_stock: "0",
  });

  const [submitLoading, setSubmitLoading] = useState(false);

  useEffect(() => {
    fetchInventory();
    fetchInventorySummary();
  }, []);

  useEffect(() => {
    // Apply filters whenever ingredients, search term, or filter options change
    filterIngredients();
  }, [ingredients, searchTerm, showInactive, showLowStock]);

  const fetchInventory = async () => {
    try {
      setLoading(true);
      const data = await inventoryService.getInventoryItems({
        include_inactive: true, // Fetch all, then filter client-side
      });

      setIngredients(data);
    } catch (err) {
      console.error("Error fetching inventory:", err);
      setError("Failed to load inventory data");
    } finally {
      setLoading(false);
    }
  };

  const fetchInventorySummary = async () => {
    try {
      const summary = await inventoryService.getInventorySummary();

      setInventorySummary(summary);
    } catch (err) {
      console.error("Error fetching inventory summary:", err);
    }
  };

  const filterIngredients = () => {
    let filtered = [...ingredients];

    // Apply search filter
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();

      filtered = filtered.filter(
        (item) =>
          item.name.toLowerCase().includes(search) ||
          (item.code && item.code.toLowerCase().includes(search)),
      );
    }

    // Apply active/inactive filter
    if (!showInactive) {
      filtered = filtered.filter((item) => item.is_active);
    }

    // Apply low stock filter
    if (showLowStock) {
      filtered = filtered.filter(
        (item) =>
          parseFloat(item.current_stock) < parseFloat(item.minimum_stock),
      );
    }

    setFilteredIngredients(filtered);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleToggleInactive = () => {
    setShowInactive(!showInactive);
  };

  const handleToggleLowStock = () => {
    setShowLowStock(!showLowStock);
  };

  const handleViewItem = (id: number) => {
    navigate(`/inventory/${id}`);
  };

  const handleCreateItem = async () => {
    try {
      setSubmitLoading(true);
      setError("");

      // Validate form
      if (!newIngredient.name.trim()) {
        setError("Name is required");
        setSubmitLoading(false);

        return;
      }

      if (!newIngredient.unit.trim()) {
        setError("Unit is required");
        setSubmitLoading(false);

        return;
      }

      // Convert string numbers to appropriate types
      const costInCents = Math.round(
        parseFloat(newIngredient.cost_per_unit) * 100,
      );

      await inventoryService.createInventoryItem({
        name: newIngredient.name,
        code: newIngredient.code,
        description: newIngredient.description,
        unit: newIngredient.unit,
        minimum_stock: parseFloat(newIngredient.minimum_stock),
        cost_per_unit: costInCents,
        supplier: newIngredient.supplier,
        current_stock: parseFloat(newIngredient.current_stock),
      });

      // Refresh inventory data
      await fetchInventory();
      await fetchInventorySummary();

      // Reset form and close modal
      setNewIngredient({
        name: "",
        code: "",
        description: "",
        unit: "unit",
        minimum_stock: "0",
        cost_per_unit: "0",
        supplier: "",
        current_stock: "0",
      });

      onAddIngredientClose();
    } catch (err) {
      console.error("Error creating ingredient:", err);
      setError("Failed to create ingredient");
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleNewIngredientChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;

    setNewIngredient({
      ...newIngredient,
      [name]: value,
    });
  };

  const handleBulkAdjustment = () => {
    onStockModalOpen();
  };

  const handleStockAdjustmentComplete = () => {
    onStockModalClose();
    fetchInventory();
    fetchInventorySummary();
  };

  return (
    <div className="p-4 space-y-6">
      <div className="flex justify-between items-center flex-wrap gap-4">
        <h1 className="text-2xl font-bold">Inventory Management</h1>
        <div className="flex gap-2">
          <Button
            color="primary"
            startContent={<Upload size={16} />}
            variant="flat"
            onPress={() => {
              // Implement import functionality
            }}
          >
            Import
          </Button>
          <Button
            color="primary"
            startContent={<Download size={16} />}
            variant="flat"
            onPress={() => {
              // Implement export functionality
            }}
          >
            Export
          </Button>
          <Button
            color="primary"
            startContent={<Plus size={16} />}
            onPress={onAddIngredientOpen}
          >
            Add Item
          </Button>
        </div>
      </div>

      {inventorySummary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="p-4">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-default-500">
                  Total Inventory Value
                </p>
                <p className="text-2xl font-semibold">
                  {formatCurrency(inventorySummary.total_value)}
                </p>
              </div>
              <span className="text-2xl text-primary">💰</span>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-default-500">Total Items</p>
                <p className="text-2xl font-semibold">
                  {inventorySummary.total_ingredients}
                </p>
              </div>
              <span className="text-2xl text-primary">📦</span>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-default-500">Low Stock Items</p>
                <p className="text-2xl font-semibold">
                  {inventorySummary.low_stock_count}
                </p>
              </div>
              {inventorySummary.low_stock_count > 0 ? (
                <AlertTriangle className="text-warning h-6 w-6" />
              ) : (
                <span className="text-2xl text-success">✓</span>
              )}
            </div>
          </Card>
        </div>
      )}

      <Card className="p-4">
        <Tabs>
          <Tab key="inventory" title="Inventory">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6 mt-4">
              <div className="w-full md:w-1/3">
                <Input
                  isClearable
                  placeholder="Search by name or code"
                  startContent={<Search size={16} />}
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onClear={() => setSearchTerm("")}
                />
              </div>

              <div className="flex gap-2">
                <Dropdown>
                  <DropdownTrigger>
                    <Button startContent={<Filter size={16} />} variant="flat">
                      Filter
                    </Button>
                  </DropdownTrigger>
                  <DropdownMenu aria-label="Filter options">
                    <DropdownItem
                      key="low-stock"
                      startContent={
                        <input
                          checked={showLowStock}
                          className="h-4 w-4"
                          type="checkbox"
                          onChange={handleToggleLowStock}
                        />
                      }
                      onPress={handleToggleLowStock}
                    >
                      Show Low Stock Only
                    </DropdownItem>
                    <DropdownItem
                      key="inactive"
                      startContent={
                        <input
                          checked={showInactive}
                          className="h-4 w-4"
                          type="checkbox"
                          onChange={handleToggleInactive}
                        />
                      }
                      onPress={handleToggleInactive}
                    >
                      Show Inactive Items
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>

                <Button
                  color="primary"
                  variant="flat"
                  onPress={handleBulkAdjustment}
                >
                  Stock Adjustment
                </Button>
              </div>
            </div>

            {error && (
              <div className="bg-danger-50 text-danger p-3 rounded-md mb-4">
                {error}
              </div>
            )}

            {loading ? (
              <div className="flex justify-center p-6">
                <Spinner size="lg" />
              </div>
            ) : filteredIngredients.length > 0 ? (
              <InventoryTable
                items={filteredIngredients}
                onEdit={handleViewItem}
                onAdjustStock={async (item) => {
                  // Implement the adjust stock logic here
                  console.log("Adjusting stock for item:", item);
                }}
                onViewHistory={async (id) => {
                  // Implement the view history logic here
                  console.log("Viewing history for item:", id);
                }}
                loading={loading}
                onToggleStatus={async (item) => {
                  // Implement the toggle status logic here
                  console.log("Toggling status for item:", item);
                }}
              />
            ) : (
              <div className="text-center p-6 border border-dashed rounded-md">
                {searchTerm || showLowStock ? (
                  <p className="text-default-500">
                    No items match your search criteria.
                  </p>
                ) : (
                  <>
                    <p className="text-default-500 mb-4">
                      No inventory items found. Add your first item to get
                      started.
                    </p>
                    <Button
                      color="primary"
                      startContent={<Plus size={16} />}
                      onPress={onAddIngredientOpen}
                    >
                      Add Item
                    </Button>
                  </>
                )}
              </div>
            )}
          </Tab>

          <Tab key="low-stock" title="Low Stock">
            <div className="mt-4">
              {loading ? (
                <div className="flex justify-center p-6">
                  <Spinner size="lg" />
                </div>
              ) : inventorySummary &&
                inventorySummary.low_stock_alerts &&
                inventorySummary.low_stock_alerts.length > 0 ? (
                <div className="space-y-4">
                  {inventorySummary.low_stock_alerts.map((item: any) => (
                    <Card key={item.ingredient_id} className="p-4">
                      <div className="flex justify-between items-center">
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-medium">
                              {item.ingredient_name}
                            </h3>
                            <Chip color="warning" size="sm">
                              {parseFloat(item.percentage).toFixed(0)}% of
                              minimum
                            </Chip>
                          </div>
                          <p className="text-sm text-default-500">
                            Current: {item.current_stock} {item.unit} | Minimum:{" "}
                            {item.minimum_stock} {item.unit}
                          </p>
                        </div>
                        <Button
                          color="primary"
                          size="sm"
                          onPress={() => handleViewItem(item.ingredient_id)}
                        >
                          Manage
                        </Button>
                      </div>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center p-6 border border-dashed rounded-md">
                  <p className="text-success">
                    All items are above minimum stock levels.
                  </p>
                </div>
              )}
            </div>
          </Tab>

          <Tab key="transactions" title="Recent Transactions">
            <div className="mt-4">
              {loading ? (
                <div className="flex justify-center p-6">
                  <Spinner size="lg" />
                </div>
              ) : inventorySummary &&
                inventorySummary.recent_transactions &&
                inventorySummary.recent_transactions.length > 0 ? (
                <div className="space-y-4">
                  {inventorySummary.recent_transactions.map(
                    (transaction: any) => (
                      <Card key={transaction.id} className="p-4">
                        <div className="flex justify-between items-center">
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-medium">
                                {transaction.ingredient_name}
                              </h3>
                              <Chip
                                color={
                                  transaction.transaction_type === "purchase"
                                    ? "success"
                                    : transaction.transaction_type === "waste"
                                      ? "danger"
                                      : "primary"
                                }
                                size="sm"
                              >
                                {transaction.transaction_type}
                              </Chip>
                            </div>
                            <p className="text-sm text-default-500">
                              {parseFloat(transaction.quantity) > 0 ? "+" : ""}
                              {transaction.quantity}{" "}
                              {transaction.ingredient_unit} |
                              {transaction.created_at}
                            </p>
                          </div>
                          <p className="text-sm">
                            {transaction.created_by_name}
                          </p>
                        </div>
                      </Card>
                    ),
                  )}
                </div>
              ) : (
                <div className="text-center p-6 border border-dashed rounded-md">
                  <p className="text-default-500">No recent transactions.</p>
                </div>
              )}
              <div className="flex justify-center mt-4">
                <Button
                  color="primary"
                  variant="flat"
                  onPress={() => navigate("/inventory/transactions")}
                >
                  View All Transactions
                </Button>
              </div>
            </div>
          </Tab>
        </Tabs>
      </Card>

      {/* Add Ingredient Modal */}
      <Modal
        isOpen={isAddIngredientOpen}
        size="lg"
        onClose={onAddIngredientClose}
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader>Add Inventory Item</ModalHeader>
              <ModalBody>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    isRequired
                    label="Name"
                    name="name"
                    value={newIngredient.name}
                    onChange={handleNewIngredientChange}
                  />
                  <Input
                    label="Code"
                    name="code"
                    placeholder="Optional"
                    value={newIngredient.code}
                    onChange={handleNewIngredientChange}
                  />
                  <div className="md:col-span-2">
                    <Input
                      label="Description"
                      name="description"
                      placeholder="Optional"
                      value={newIngredient.description}
                      onChange={handleNewIngredientChange}
                    />
                  </div>
                  <Input
                    isRequired
                    label="Unit"
                    name="unit"
                    placeholder="kg, liters, pieces, etc."
                    value={newIngredient.unit}
                    onChange={handleNewIngredientChange}
                  />
                  <Input
                    label="Cost per Unit ($)"
                    name="cost_per_unit"
                    startContent={
                      <div className="pointer-events-none flex items-center">
                        <span className="text-default-400 text-small">$</span>
                      </div>
                    }
                    type="number"
                    value={newIngredient.cost_per_unit}
                    onChange={handleNewIngredientChange}
                  />
                  <Input
                    label="Minimum Stock"
                    name="minimum_stock"
                    type="number"
                    value={newIngredient.minimum_stock}
                    onChange={handleNewIngredientChange}
                  />
                  <Input
                    label="Initial Stock"
                    name="current_stock"
                    type="number"
                    value={newIngredient.current_stock}
                    onChange={handleNewIngredientChange}
                  />
                  <Input
                    label="Supplier"
                    name="supplier"
                    placeholder="Optional"
                    value={newIngredient.supplier}
                    onChange={handleNewIngredientChange}
                  />
                </div>

                {error && (
                  <div className="text-danger text-sm mt-2">{error}</div>
                )}
              </ModalBody>
              <ModalFooter>
                <Button variant="flat" onPress={onClose}>
                  Cancel
                </Button>
                <Button
                  color="primary"
                  isLoading={submitLoading}
                  onPress={handleCreateItem}
                >
                  Create Item
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      {/* Stock Adjustment Modal */}
      <StockAdjustmentModal
        isOpen={isStockModalOpen}
        onClose={onStockModalClose}
        onSave={handleStockAdjustmentComplete}
        loading={loading}
        inventoryItems={ingredients}
      />
    </div>
  );
}
